import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'tea-component';
import CommonBarAndLine from '../../components/CommonBarAndLine';
import {
  echartsFormatterFunc,
  dealwithTableCommentData,
  dealwithGrowthChartRatio,
} from '../../utils';
import SubMenuCard from '../SubMenuCard';
import TableCard from '../TableCard';
import { channelColorTextMap, tabMap, tabOptions } from '../../constants';
import {
  accountsAudiencePerformanceTipsText,
  accountsFollowersTipsText,
  accountsGrowthOfFollowersText,
  accountsPublishingPerformanceTipsText,
} from '@/pages/opinion/components/BubbleCommon/copywritingKey/accounts';
import BubbleCommon, {
  TipsMapType,
} from '@/pages/opinion/components/BubbleCommon';
import moment from 'moment';
import CardCommon from '@/pages/opinion/components/CardCommon';
import { handleMentionData, supplementSingleData } from '@/pages/opinion/utils';
import {
  getFollowersDataInfo,
  getPublishingTimelineDataInfo,
} from '@/pages/opinion/services/accounts';
import { getNeedTime } from '@/pages/opinion/utils/time';
import { formatNewData } from '@/pages/opinion/utils/data';
import { ServerTimeType } from '@/pages/opinion/components/FilterCommon/types';
import { downloadAction } from '@/pages/opinion/components/DownloadCommon/download-page';
import { useIntl } from 'umi';
import {
  channelInfo,
  dealwithChannelChartKey,
} from '@/pages/opinion/components/ChannelLabel';
import { barIcon, lineIcon, tableIcon } from '@/pages/opinion/constants/chart';

export const initChartDataMap = (hasFollowers: boolean = true) => {
  let result: {
    [key: string]: { x: Array<string>; y: Array<any>; tableRecord: Array<any> };
  } = {};
  let keys = tabOptions.map((item) => item.value);
  if (hasFollowers) {
    keys.push(...['followers', 'followers_growth']);
  }
  keys.forEach((item) => {
    result[item] = {
      x: [],
      y: [],
      tableRecord: [],
    };
  });
  return result;
};

/**
 * 这个组件，主要是图表分离
 * 然后图表的数据请求放在该组件，防止首页内容太过庞大
 * @param param0
 * @returns
 */
export default function ChartCard({ keepParams, type }: any) {
  const sortChartData = (newArr: any[], sorts: string | any[]) => {
    newArr.sort((a, b) => {
      return a.name.toLocaleLowerCase() === 'others'
        ? -1
        : b.name.toLocaleLowerCase() === 'others'
          ? 1
          : sorts.indexOf(a.name) - sorts.indexOf(b.name);
    });
  };

  // 处理增长量数据方法
  const dealwithGrowth = (
    element: Array<any>,
    timeline_of_start_time: string,
    timeline_of_end_time: string,
    channel_account_split: string,
    sorts: Array<string>,
    dateType: ServerTimeType,
    needTime: any,
  ) => {
    // 处理增长量数据
    let { table } = dealwithGrowthChartRatio(
      timeline_of_start_time,
      timeline_of_end_time,
      element,
      dateType,
    );
    let { newArr, timeArr } = handleMentionData([...(table || [])], needTime);
    newArr = newArr.map((item: { name: string | number }) => {
      return {
        ...item,
        legendName: item.name,
        splitText: channel_account_split,
        ...(type === 'account' ? {} : channelColorTextMap[item.name] || {}),
      };
    });
    // 将other排序到最后
    sortChartData(newArr, sorts);
    return [timeArr, newArr, table];
  };

  // 处理通用数据方法
  const dealwithCommon = (
    element: Array<any>,
    channel_account_split: string,
    sorts: Array<string>,
    dateType: ServerTimeType,
    needTime: any,
  ) => {
    let { newArr, timeArr } = handleMentionData(element, needTime);
    // 重写后可将改操作一次完成
    newArr = newArr.map((item: any) => {
      return {
        ...item,
        splitText: channel_account_split,
        legendName: item.name,
        ...(type === 'account' ? {} : channelColorTextMap[item.name] || {}),
      };
    });
    // 将other排序到最后
    sortChartData(newArr, sorts);

    let tableRecord = dealwithTableCommentData(element, dateType);

    return [timeArr, newArr, tableRecord];
  };

  /***********************Engagements*****************************/
  const [engagementsLoading, setEngagementsLoading] = useState<boolean>(false);
  const [engagementsError, setEngagementsError] = useState<boolean>(false);
  const [engagementsSplitText, setEngagementsSplitText] = useState('#_to_#');
  const [engagementsDataMap, setEngagementsDataMap] = useState(
    initChartDataMap(false),
  );
  const [topRightDataType, setTopRightDataType] = useState('engagements');

  const { formatMessage } = useIntl();

  const engagementsMap = useMemo(() => {
    return {
      title: formatMessage({ id: 'Publishing Performance' }),
      downloadSheetName: 'Publishing Performance',
      default: SubMenuCard,
      isLeft: false,
      downloadFunc: 'getPublishingTimelineDataInfo',
      setDataType: (value: any) => {
        setTopRightDataType(value);
      },
      dataType: topRightDataType,
      dataMap: engagementsDataMap,
    };
  }, [engagementsDataMap, topRightDataType, formatMessage]);
  // 获取右上角的那个图表数据
  const getTopRightTimeline = async () => {
    setEngagementsLoading(true);
    setEngagementsError(false);
    const { channel, social_media_by_account, region, ...otherParams } =
      keepParams;
    getPublishingTimelineDataInfo({
      ...otherParams,
      accounts: social_media_by_account,
      model_type: type,
      regions: region,
      page: 1,
      page_size: 9,
      channels: channel,
      metrics: tabOptions.map((item) => item.value),
      need_total_link: false,
      sort_model: 'desc',
    }).then((data: any) => {
      let dateType = keepParams.date_type;
      let localNeedTime = getNeedTime(dateType) as any;
      let {
        list,
        timeline_of_start_time,
        timeline_of_end_time,
        channel_account_split,
        sorts,
      } = data || {};
      list = list || {};
      let resultMap = initChartDataMap(false);
      for (const key in list) {
        const element = list[key] || {};
        let newList = formatNewData((element.timeline || []) as any);
        newList = supplementSingleData(
          timeline_of_start_time,
          timeline_of_end_time,
          newList,
          dateType,
          sorts,
        );
        const [x, y, tableRecord] = dealwithCommon(
          newList,
          channel_account_split,
          sorts,
          dateType,
          localNeedTime,
        );
        resultMap[key] = { x, y, tableRecord };
      }
      setEngagementsSplitText(channel_account_split);
      setEngagementsDataMap(resultMap);
      setEngagementsLoading(false);
    });
  };
  /***********************Engagements*****************************/

  /***********************Followers*****************************/
  const [treeChartDataMap, setTreeChartDataMap] = useState(initChartDataMap());
  const [followersLoading, setFollowersLoading] = useState<boolean>(false);
  const [followersError, setFollowersError] = useState<boolean>(false);
  const [followersSplitText, setFollowersSplitText] = useState('#_to_#');
  const [otherDataType, setOtherDataType] = useState('engagements');
  // 获取Followers图表数据
  const getTreeChartDataTimeline = async () => {
    setFollowersLoading(true);
    setFollowersError(false);
    const { channel, social_media_by_account, region, ...otherParams } =
      keepParams;
    getFollowersDataInfo({
      ...otherParams,
      accounts: social_media_by_account,
      model_type: type,
      regions: region,
      metrics: [...tabOptions.map((item) => item.value), 'followers'],
      page: 1,
      page_size: 9,
      channels: channel,
      need_total_link: false,
      sort_model: 'desc',
    }).then((data: any) => {
      let dateType = keepParams.date_type;
      let localNeedTime = getNeedTime(dateType) as any;
      let {
        list,
        timeline_of_start_time,
        timeline_of_end_time,
        channel_account_split,
        sorts,
      } = data || {};
      list = list || {};
      const resultMap = initChartDataMap();
      for (const key in list) {
        const element = list[key];
        // 后期需要对handleMentionData方法进行重写
        let newList = formatNewData((element?.timeline || []) as any);

        const localNewList = supplementSingleData(
          timeline_of_start_time,
          timeline_of_end_time,
          newList,
          dateType,
          sorts,
        );
        // 非增长量数据。Followers和其他tab都要使用
        const [x, y, tableRecord] = dealwithCommon(
          localNewList,
          channel_account_split,
          sorts,
          dateType,
          localNeedTime,
        );

        if (key === 'followers') {
          const local_start_time = moment(timeline_of_start_time)
            .subtract(1, 'days')
            .format('YYYY-MM-DD');
          let localList = supplementSingleData(
            local_start_time,
            timeline_of_end_time,
            newList,
            dateType,
            sorts,
          );
          // 增长量数据。Followers Growth使用
          const [gx, gy, gTableRecord] = dealwithGrowth(
            localList,
            timeline_of_start_time,
            timeline_of_end_time,
            channel_account_split,
            sorts,
            dateType,
            localNeedTime,
          );
          resultMap.followers_growth = {
            x: gx,
            y: gy,
            tableRecord: gTableRecord,
          };
        }
        resultMap[key] = { x: x, y: y, tableRecord: tableRecord };
      }
      setTreeChartDataMap(resultMap);
      setFollowersSplitText(channel_account_split);
      setFollowersLoading(false);
    });
  };

  const [followersMap, growthOfFollowersMap, growthOfEngagementsMap] =
    useMemo(() => {
      let { followers_growth, followers, ...otherChartData } = treeChartDataMap;
      return [
        {
          title: formatMessage({ id: 'Followers' }),
          downloadSheetName: 'Followers',
          default: CommonBarAndLine,
          table: TableCard,
          xAxisData: followers?.x || [],
          seriesDataArray: followers?.y || [],
          records: followers?.tableRecord || [],
          downloadFunc: 'getFollowersDataInfo',
          theThirdColumnConfig: {
            key: 'value',
            header: formatMessage({ id: 'Followers' }),
          },
          isLeft: true,
          tableHeight: 308,
          noData: followers.y.length === 0,
        },
        {
          title: formatMessage({ id: 'Growth of Followers' }),
          downloadSheetName: 'Growth of Followers',
          default: CommonBarAndLine,
          table: TableCard,
          defaultType: 'line',
          xAxisData: followers_growth?.x || [],
          seriesDataArray: followers_growth?.y || [],
          records: followers_growth?.tableRecord || [],
          isLeft: true,
          downloadFunc: 'getFollowersDataInfo',
          theThirdColumnConfig: {
            key: 'value',
            width: 170,
            header: formatMessage({ id: 'Growth of Followers' }),
          },
          tableHeight: 308,
          noShare: true,
          isCenterAxis: true,
          noData: followers_growth.y.length === 0,
        },
        {
          title: formatMessage({ id: 'Audience Performance' }),
          downloadSheetName: 'Audience Performance',
          default: SubMenuCard,
          isLeft: false,
          noShare: true,
          dataMap: otherChartData,
          isCenterAxis: false,
          downloadFunc: 'getFollowersDataInfo',
          setDataType: (value: any) => {
            setOtherDataType(value);
          },
          dataType: otherDataType,
        },
      ];
    }, [treeChartDataMap, otherDataType, formatMessage]);
  /***********************Followers*****************************/

  useEffect(() => {
    if (Object.keys(keepParams || {}).length > 0) {
      getTopRightTimeline();
      getTreeChartDataTimeline();
    }
  }, [keepParams, type]);

  return (
    <Row>
      <ColElementItem
        data={followersMap}
        {...{
          loading: followersLoading,
          apiError: followersError,
          splitText: followersSplitText,
          metric: 'followers',
          tips: accountsFollowersTipsText,
          keepParams,
        }}
        type={type}
      ></ColElementItem>
      <ColElementItem
        data={engagementsMap}
        {...{
          loading: engagementsLoading,
          inner: true,
          apiError: engagementsError,
          splitText: engagementsSplitText,
          metric: topRightDataType,
          tips: accountsPublishingPerformanceTipsText,
          keepParams,
        }}
        type={type}
      ></ColElementItem>
      <ColElementItem
        data={growthOfFollowersMap}
        {...{
          loading: followersLoading,
          apiError: followersError,
          splitText: followersSplitText,
          metric: 'followers',
          tips: accountsGrowthOfFollowersText,
          keepParams,
        }}
        type={type}
        defaultChartType="line"
      ></ColElementItem>

      <ColElementItem
        data={growthOfEngagementsMap}
        {...{
          loading: followersLoading,
          inner: true,
          apiError: followersError,
          splitText: followersSplitText,
          metric: otherDataType,
          tips: accountsAudiencePerformanceTipsText,
          keepParams,
        }}
        type={type}
      ></ColElementItem>
    </Row>
  );
}

export function ColElementItem({
  data,
  type,
  loading,
  inner = false,
  apiError = false,
  splitText = '#_to_#',
  metric,
  tips,
  keepParams,
  defaultChartType = 'bar',
}: {
  data: any;
  type: string;
  loading: boolean;
  apiError: boolean;
  splitText: string;
  metric: string;
  tips: TipsMapType;
  inner?: boolean;
  keepParams: any;
  defaultChartType?: string;
}) {
  const download = () => {
    const { channel, region, social_media_by_account, ...otherParams } =
      keepParams;
    const { downloadFunc, downloadSheetName } = data;
    let newParams = {
      ...otherParams,
      accounts: social_media_by_account,
      model_type: type,
      channels: channel,
      page: 1,
      name: downloadSheetName,
      page_size: 9,
      regions: region,
      metrics: [metric],
      need_total_link: false,
      sort_model: 'desc',
      split_text: splitText,
      noChangePageSize: true,
    };
    downloadAction({
      limit: 150000,
      downLoadParams: newParams,
      downloadFunc: downloadFunc,
      sheetName: downloadSheetName,
      simpleName: type === 'account' ? 'By Account' : 'By Channel',
    });
  };
  const [chartType, setChartType] = useState<any>(defaultChartType);
  const { formatMessage } = useIntl();
  const Component = data[chartType] || data.default;
  return (
    <Col span={12} key={data.title}>
      <CardCommon
        className="dbo-accounts-chartbox"
        loading={inner ? false : loading}
        bodyStyle={{ height: 392 }}
        loadingHeight={300}
        emptyHeight={300}
        defaultOperation={chartType}
        empty={inner ? false : data.noData}
        handleOperationsOnClick={(item) => {
          setChartType(item.symbol);
        }}
        title={
          <BubbleCommon showIcon={false} tipsMap={tips}>
            {data.title}
          </BubbleCommon>
        }
        operations={[barIcon, lineIcon, tableIcon]}
        hasDownload
        handleDownloadOnClick={() => download()}
      >
        <Component
          chartType={chartType}
          apiError={apiError}
          lineLegendFormatter={(name: any) => {
            if (type === 'account' && name?.toLocaleLowerCase() !== 'others') {
              const [channel, newName] = name?.split(splitText) || [];
              const { name: channelName } = channelInfo(channel);
              const finalName = newName.replace(channel, channelName);
              return `{${dealwithChannelChartKey(
                channel,
              )}| } {name| ${finalName}}`;
            }
            if (name?.toLocaleLowerCase() === 'others') {
              return formatMessage({ id: 'Others' });
            }
            const { name: channelName } = channelInfo(name);
            return channelName;
          }}
          type={type}
          splitText={splitText}
          tooltipFormatterFunc={(params: any) =>
            echartsFormatterFunc(
              params,
              splitText,
              type,
              data.noShare,
              tabMap[metric]
                ? formatMessage({ id: tabMap[metric] })
                : data.title,
              formatMessage({ id: 'Share-1' }),
              formatMessage({ id: 'Sum' }),
              keepParams.date_type,
              formatMessage,
            )
          }
          height={318}
          loading={loading}
          tipsHeight={230}
          isStack={chartType === 'bar'} // isStack 下面两个图都是堆叠状态，上面两图只有bar时才为堆叠状态
          {...data}
        ></Component>
      </CardCommon>
    </Col>
  );
}
